import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Accordion,
	AccordionActions,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import CancellationsApi from '../../../api/CancellationsApi';
import EndorsementsApi from '../../../api/EndorsementsApi';
import RenewalsApi from '../../../api/RenewalsApi';
import { AuthData } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { dayjs } from '../../../date-util';
import { PolicyModification } from '../../../types/policy';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import {
	DATE_PRECISION,
	DATE_PRECISION_INPUT_FORMAT,
} from '../../fastForm/data/constants';
import { PolicyModificationNames } from './PolicyModificationEnum';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
}

type ExpandedPanels = { [key: string]: boolean };

const PolicyHistoryView = (props: Props) => {
	const { authData, policyData } = props;
	const { policy } = policyData;
	const { hasPermission } = authData;

	const [expandedPanels, setExpandedPanels] = useState<ExpandedPanels>({});
	const [expandAll, setExpandAll] = useState(false);

	const [sortBy, setSortBy] = useState<string>('createdDate');
	const [modifications, setModifications] = useState<PolicyModification[]>(
		policy.modifications,
	);

	const title = 'Policy History';

	const { data: renewalsResponse } = useQuery({
		queryKey: ['renewals', policy.locator, authData.auth.authorizationToken],
		enabled: hasPermission('renewal.fetch'),
		staleTime: 1000 * 60 * 10, // 10 minutes
		queryFn: () =>
			RenewalsApi.getRenewalsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
	});

	const { data: endorsementsResponse } = useQuery({
		queryKey: [
			'endorsements',
			policy.locator,
			authData.auth.authorizationToken,
		],
		staleTime: 1000 * 60 * 10, // 10 minutes
		enabled: hasPermission('endorsement.fetch'),
		queryFn: () =>
			EndorsementsApi.getEndorsementsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
	});

	const { data: cancellationsResponse } = useQuery({
		queryKey: [
			'cancellations',
			policy.locator,
			authData.auth.authorizationToken,
		],
		staleTime: 1000 * 60 * 10, // 10 minutes
		enabled: hasPermission('cancellation.fetch'),
		queryFn: () =>
			CancellationsApi.getCancellationsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
	});

	const renewals = renewalsResponse?.data || [];
	const endorsements = endorsementsResponse?.data || [];
	const cancellations = cancellationsResponse?.data || [];

	const reinstatements = cancellations
		? cancellations
				.map((cancellation) => {
					return cancellation.reinstatement ?? null;
				})
				.filter((reinstatement) => reinstatement !== null)
		: [];

	useEffect(() => {
		if (sortBy === 'effectiveDate') {
			setModifications(
				[...modifications].sort(
					(a, b) =>
						parseInt(b.effectiveTimestamp) - parseInt(a.effectiveTimestamp),
				),
			);
		} else {
			// sortBy === 'createdDate'
			// original modifications array is in created by date order
			setModifications(policy.modifications);
		}
	}, [sortBy]);

	const handleChangeExpanded =
		(panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
			setExpandedPanels({ ...expandedPanels, [panel]: newExpanded });
		};

	const handleExpandAll = () => {
		let newExpandedPanels: ExpandedPanels = {};
		if (!Object.values(expandedPanels).find((open) => open === true)) {
			// expand all
			modifications.forEach((modification) => {
				newExpandedPanels = {
					...newExpandedPanels,
					[modification.locator]: true,
				};
			});
		} else {
			// expand or collapse all panels that don't match expandAll value
			Object.keys(expandedPanels).forEach(
				(panelKey) => (newExpandedPanels[panelKey] = !expandAll),
			);
		}
		setExpandedPanels(newExpandedPanels);
		setExpandAll(!expandAll);
	};

	const generateDescription = (
		modification: PolicyModification,
	): JSX.Element => {
		const issuedDate = modification.issuedTimestamp
			? dayjs(parseInt(modification.issuedTimestamp)).format('YYYY/MM/DD')
			: undefined;
		const effectiveDate = dayjs(
			parseInt(modification.effectiveTimestamp),
		).format('YYYY/MM/DD');
		const createdDate = dayjs(parseInt(modification.createdTimestamp)).format(
			'YYYY/MM/DD',
		);

		const resourcesAsOfDate = modification.resourcesAsOfTimestamp
			? dayjs(parseInt(modification.resourcesAsOfTimestamp)).format(
					DATE_PRECISION_INPUT_FORMAT[DATE_PRECISION.MINUTE],
			  )
			: undefined;

		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				<div style={{ display: 'flex', justifyContent: 'space-around' }}>
					<Typography>
						<span style={{ fontWeight: 500 }}>Created Date:</span> {createdDate}
					</Typography>
					{issuedDate ? (
						<Typography>
							<span style={{ fontWeight: 500 }}>Issued Date:</span> {issuedDate}
						</Typography>
					) : null}
					<Typography>
						<span style={{ fontWeight: 500 }}>Effective Date:</span>{' '}
						{effectiveDate}
					</Typography>
					{resourcesAsOfDate ? (
						<Typography>
							<span style={{ fontWeight: 500 }}>Resource Group:</span>{' '}
							{resourcesAsOfDate}
						</Typography>
					) : null}
				</div>
			</div>
		);
	};

	const getLocator = (modification: PolicyModification): string => {
		const modificationName =
			modification.name as keyof typeof PolicyModificationNames;
		let locator = '';

		switch (PolicyModificationNames[modificationName]) {
			// Policy Creation
			case PolicyModificationNames['modification.policy.create']:
				locator = modification.policyLocator;
				break;
			// Renewal
			case PolicyModificationNames['modification.policy.renew']:
				locator =
					renewals.length > 0
						? renewals.find((renewal) => {
								return (
									renewal.createdTimestamp === modification.createdTimestamp &&
									renewal.issuedTimestamp === modification.issuedTimestamp &&
									renewal.renewalStartTimestamp ===
										modification.effectiveTimestamp
								);
						  }).locator
						: '';
				break;
			// Cancellation
			case PolicyModificationNames['modification.policy.cancel']:
				locator =
					cancellations.length > 0
						? cancellations.find((cancellation) => {
								return (
									cancellation.createdTimestamp ===
										modification.createdTimestamp &&
									cancellation.issuedTimestamp ===
										modification.issuedTimestamp &&
									cancellation.effectiveTimestamp ===
										modification.effectiveTimestamp
								);
						  }).locator
						: '';
				break;
			// Reinstatement
			case PolicyModificationNames['modification.policy.reinstate']:
				locator =
					reinstatements.length > 0
						? reinstatements.find((reinstatement) => {
								return (
									reinstatement.createdTimestamp ===
										modification.createdTimestamp &&
									reinstatement.issuedTimestamp ===
										modification.issuedTimestamp &&
									reinstatement.effectiveTimestamp ===
										modification.effectiveTimestamp
								);
						  }).locator
						: '';
				break;
			// Endorsement
			case undefined:
				locator =
					endorsements.length > 0
						? endorsements.find((endorsement) => {
								return (
									endorsement.createdTimestamp ===
										modification.createdTimestamp &&
									endorsement.issuedTimestamp ===
										modification.issuedTimestamp &&
									endorsement.startTimestamp === modification.effectiveTimestamp
								);
						  }).locator
						: '';
				break;
		}
		return locator;
	};

	const generateUrl = (modification: PolicyModification): string => {
		const modificationName =
			modification.name as keyof typeof PolicyModificationNames;
		let url = '';
		const locator = getLocator(modification);

		switch (PolicyModificationNames[modificationName]) {
			// Policy Creation
			case PolicyModificationNames['modification.policy.create']:
				url = `/policy/${modification.policyLocator}/details`;
				break;
			// Renewal
			case PolicyModificationNames['modification.policy.renew']:
				url = locator
					? `/policy/${modification.policyLocator}/renewals/${locator}`
					: '';
				break;
			// Cancellation
			case PolicyModificationNames['modification.policy.cancel']:
				url = locator
					? `/policy/${modification.policyLocator}/cancellations/${locator}`
					: '';
				break;
			// Reinstatement
			case PolicyModificationNames['modification.policy.reinstate']:
				// locate reinstatement object and get cancellation locator
				const reinstatementRef = reinstatements.find(
					(reinstatement) => reinstatement.locator === locator,
				);
				url =
					locator && reinstatementRef
						? `/policy/${modification.policyLocator}/cancellations/${reinstatementRef.cancellationLocator}/reinstatements/${locator}`
						: '';
				break;
			// Endorsement
			case undefined:
				url = locator
					? `/policy/${modification.policyLocator}/endorsements/${locator}`
					: '';
				break;
		}
		return url;
	};

	const renderGroup = (modification: PolicyModification): JSX.Element => {
		const modificationLocator = modification.locator;
		const eventLocator = getLocator(modification);
		const modificationName =
			modification.name as keyof typeof PolicyModificationNames;
		const effectiveDate = dayjs(
			parseInt(modification.effectiveTimestamp),
		).format('YYYY/MM/DD');
		const issuedDate = dayjs(parseInt(modification.issuedTimestamp)).format(
			'YYYY/MM/DD',
		);
		const locatorTitle = eventLocator ? `(${eventLocator})` : '';
		const eventTitle = `${
			PolicyModificationNames[modificationName] ?? 'Policy Change'
		}`;
		let url = generateUrl(modification);

		return (
			<Accordion
				key={modificationLocator}
				expanded={
					expandedPanels[modificationLocator]
						? expandedPanels[modificationLocator]
						: false
				}
				onChange={handleChangeExpanded(modificationLocator)}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls={`${modificationLocator}-content`}
					id={`panel${modificationLocator}-header`}
				>
					{
						<Box
							sx={{
								width: '40%',
								flexShrink: 0,
								display: 'flex',
								alignItems: 'baseline',
								fontWeight: '500',
							}}
						>
							<Typography sx={{ fontWeight: '500', mr: 1 }}>
								{eventTitle}
							</Typography>
							{locatorTitle ? locatorTitle : ''}
						</Box>
					}
					{!expandedPanels[modificationLocator] ? (
						<Typography sx={{ width: '40%' }}>
							<span style={{ fontWeight: 500 }}>Issued:</span> {issuedDate}
						</Typography>
					) : null}
					{!expandedPanels[modificationLocator] ? (
						<Typography>
							<span style={{ fontWeight: 500 }}>Effective:</span>{' '}
							{effectiveDate}
						</Typography>
					) : null}
				</AccordionSummary>
				<AccordionDetails>{generateDescription(modification)}</AccordionDetails>
				{
					// show "View [modification name]" button only on those modifications with valid URLs
					url !== '' ? (
						<AccordionActions>
							<Button
								onClick={() => {
									window.open(url, '_top');
								}}
								data-url={url}
							>
								View{' '}
								{PolicyModificationNames[modificationName] ?? 'Policy Change'}
							</Button>
						</AccordionActions>
					) : null
				}
			</Accordion>
		);
	};

	const renderGroups = () => {
		return modifications.map((modification) => renderGroup(modification));
	};

	const renderControls = () => {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<FormControl component='fieldset'>
					<FormLabel component='legend'>Sort by:</FormLabel>
					<RadioGroup
						row
						aria-label='Sort'
						name='row-radio-buttons-group'
						value={sortBy}
						onChange={(e) => {
							setSortBy(e.target.value);
						}}
					>
						<FormControlLabel
							value='createdDate'
							control={
								<Radio
									sx={{
										'& .MuiSvgIcon-root': {
											fontSize: 20,
										},
									}}
								/>
							}
							label='Created Date'
						/>
						<FormControlLabel
							value='effectiveDate'
							control={
								<Radio
									sx={{
										'& .MuiSvgIcon-root': {
											fontSize: 20,
										},
									}}
								/>
							}
							label='Effective Date'
						/>
					</RadioGroup>
				</FormControl>
				<Button
					style={{ marginLeft: 'auto', height: '50%' }}
					onClick={handleExpandAll}
					endIcon={
						<ExpandMoreIcon
							style={{
								transform: expandAll ? 'rotate(180deg)' : 'rotate(0deg)',
								transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
							}}
						/>
					}
				>
					{expandAll ? 'Collapse All' : 'Expand All'}
				</Button>
			</div>
		);
	};

	return (
		<>
			<StyledTitleContainer>
				<StyledTitle>{title}</StyledTitle>
			</StyledTitleContainer>
			<hr />
			{renderControls()}
			<StyledSectionContainer>{renderGroups()}</StyledSectionContainer>
		</>
	);
};

export default withPolicyContext<Props>(PolicyHistoryView);
